// ----------------------------------------------------------------------
// Shared Section Types
// ----------------------------------------------------------------------

import type { IconifyName } from 'src/components/iconify/register-icons';

import contactsAsset from '../assets/icons/contacts.svg';
import outlookAsset from '../assets/icons/microsoft-outlook.svg';
import outlookCalendarAsset from '../assets/icons/outlook-calendar.svg';

// Skill record from backend
/** Whether Ari runs what the author sees. The skills API uses the same words. */
export type SkillLiveState = 'not_published' | 'changes_not_live' | 'live';

export interface SkillRecord {
  id: string;
  name: string;
  description: string | null;
  instructions: string | null;
  /** The SKILL.md being edited. Only the author's own skills carry it. */
  draft_md?: string | null;
  /** null for a skill from the old page. */
  live_state?: SkillLiveState | null;
  visibility?: 'personal' | 'workspace';
  /** Made by someone else and shared with the user's workspace. */
  from_workspace?: boolean;
  /** Only for a skill that was never published. */
  has_errors?: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// Connector types
export interface Connector {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  isConnected: boolean;
  isLoading?: boolean;
  isImplemented?: boolean;
}

// Connector record from backend
export interface ConnectorRecord {
  id: string;
  name: string;
  description: string | null;
  type: string;
  config: Record<string, unknown>;
  tags: string[];
  requires_token: boolean;
  configurable?: boolean;
  iconify_logo: string | null;
  auth0_connection: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CustomMcpConnectorRecord extends ConnectorRecord {
  type: 'mcp';
  config: ConnectorRecord['config'] & { source: 'dynamic' };
}

export function isCustomMcpConnector(connector: ConnectorRecord): connector is CustomMcpConnectorRecord {
  return connector.type === 'mcp' && connector.config.source === 'dynamic' && !isCredentialConnector(connector);
}

export type CredentialPlacement = { kind: 'header'; name: string; scheme?: string } | { kind: 'query'; param: string };

export interface CredentialConnectorRecord extends ConnectorRecord {
  config: ConnectorRecord['config'] & {
    source: 'dynamic';
    credential_ref: string;
    placement?: CredentialPlacement;
    host?: string;
    mcp_url?: string;
    connected_account_id?: string;
  };
}

const CREDENTIAL_SCHEME = /^[a-z]+:/;

/** A row whose secret is a pasted key. An OAuth MCP row on the broker is an MCP row to the UI. */
export function isCredentialConnector(connector: ConnectorRecord): connector is CredentialConnectorRecord {
  return connector.config?.source === 'dynamic' && String(connector.config?.credential_ref ?? '').startsWith('apikey:');
}

export function credentialHostOf(connector: CredentialConnectorRecord): string {
  return connector.config.host || connector.config.credential_ref.replace(CREDENTIAL_SCHEME, '');
}

/** A row the user created: a custom MCP server or an API key. */
export function isCustomConnector(connector: ConnectorRecord): boolean {
  return isCustomMcpConnector(connector) || isCredentialConnector(connector);
}

/** The URL a custom connector points at. An API-key host counts as `https://<host>/`. */
export function customConnectorUrlOf(connector: ConnectorRecord): string | null {
  const { mcp_url: mcpUrl, host } = connector.config ?? {};
  if (typeof mcpUrl === 'string' && mcpUrl) return mcpUrl;
  if (typeof host === 'string' && host) return `https://${host}/`;
  if (isCredentialConnector(connector)) return `https://${credentialHostOf(connector)}/`;
  return null;
}

const CONNECTOR_BRAND_COLORS: Record<string, string> = {
  microsoft_outlook: '#0A2472',
  microsoft_outlook_calendar: '#0A2472',
  microsoft_outlook_emails: '#0A2472',
  microsoft_teams: '#312e81',
  microsoft_onedrive: '#0A2472',
  microsoft_sharepoint: '#0A2472',
  microsoft_contacts: '#1E293B',
  google_calendar: '#1e293b',
  google_gmail: '#1e293b',
  google_contacts: '#1e293b',
};

export function getConnectorBgColor(connectorId: string, fallback: string): string {
  return CONNECTOR_BRAND_COLORS[connectorId] ?? fallback;
}

export function getConnectorIcon(
  connector: Pick<ConnectorRecord, 'id' | 'iconify_logo'> & Partial<Pick<ConnectorRecord, 'type'>>
): IconifyName {
  // Outlook connectors render via a static SVG asset (see connectorAssetUrl);
  // iconify_logo only kicks in when the connector explicitly opts in.
  if (connector.iconify_logo && !connector.id.startsWith('microsoft_outlook'))
    return connector.iconify_logo as IconifyName;
  if (connector.type === 'mcp') return 'eva:link-2-fill';
  if (connector.id === 'microsoft_teams') return 'logos:microsoft-teams';
  return connector.id.startsWith('microsoft_') ? 'logos:microsoft-icon' : 'logos:google-icon';
}

// Static SVG assets that should be rendered via <img> instead of Iconify —
// keeps the Outlook 2025 logo identical to the one used in Email / Calendar
// scene headers. Imported rather than hardcoded as '/icons/*.svg' so each
// consuming app's bundler resolves the URL: apps/ari-cloud/public/icons shipped
// only microsoft-outlook.svg, so the contacts and outlook-calendar literals fell
// through to its SPA index fallback and rendered as broken images.
export function getConnectorAssetUrl(connectorId: string): string | null {
  if (connectorId === 'microsoft_outlook_calendar') return outlookCalendarAsset;
  if (connectorId === 'microsoft_contacts') return contactsAsset;
  if (connectorId.startsWith('microsoft_outlook')) return outlookAsset;
  return null;
}

export enum ConnectorTokenStatus {
  Valid = 'valid',
  Expired = 'expired',
  Error = 'error',
}

export type ConnectorState = 'connected' | 'expired' | 'disconnected' | 'error' | 'off';

/**
 * A connector is "connected" only when:
 *   - the user has it enabled (their intent), AND
 *   - if it requires an upstream token: the connector status endpoint reports it as live AND
 *     the last known token_status is `valid`.
 *
 * Connectors with `requires_token=false` (local/MCP-style) skip the upstream
 * checks entirely — they don't have an upstream token that can expire.
 *
 * For token-requiring connectors, rows in `user_connectors` are inserted with
 * `token_status='valid'` (the column default and what `callbackHandler` writes
 * after a successful OAuth callback). A missing entry in the statuses map
 * defaults to `valid` — that case happens for newly-enabled rows the catalog
 * query has already returned but `user_connector_statuses` hasn't been
 * populated for (brief race between optimistic enable and catalog refetch).
 */
export function isConnectorConnected(
  connector: Pick<ConnectorRecord, 'id' | 'requires_token'>,
  connectedConnectorIds: Set<string>,
  userEnabledConnectorIds: Set<string>,
  userConnectorStatuses: Map<string, ConnectorTokenStatus>
): boolean {
  if (!userEnabledConnectorIds.has(connector.id)) return false;
  if (!connector.requires_token) return true;
  if (!connectedConnectorIds.has(connector.id)) return false;
  const status = userConnectorStatuses.get(connector.id) ?? ConnectorTokenStatus.Valid;
  return status === ConnectorTokenStatus.Valid;
}

/**
 * Resolves a connector's UI state. 'off' wins: the row keeps its auth, but the user told Ari not to use it.
 * `isConnectorConnected` ignores 'off', so an off row keeps its place in its list.
 */
export function getConnectorState(
  connector: Pick<ConnectorRecord, 'id' | 'requires_token'>,
  connectedConnectorIds: Set<string>,
  userEnabledConnectorIds: Set<string>,
  userConnectorStatuses: Map<string, ConnectorTokenStatus>,
  userDisabledConnectorIds: Set<string> = new Set()
): ConnectorState {
  if (userDisabledConnectorIds.has(connector.id)) return 'off';
  if (!userEnabledConnectorIds.has(connector.id)) return 'disconnected';
  if (!connector.requires_token) return 'connected';

  const status = userConnectorStatuses.get(connector.id) ?? ConnectorTokenStatus.Valid;
  if (status === ConnectorTokenStatus.Expired) return 'expired';
  if (status === ConnectorTokenStatus.Error) return 'error';

  // status === 'valid' still requires the connector-status signal.
  if (!connectedConnectorIds.has(connector.id)) return 'expired';

  return 'connected';
}

// Chat types
export interface ChatSummary {
  id: string;
  title: string | null;
  updated_at: string;
  messageCount?: number;
  device_type?: string;
  /**
   * Optimistic rows are non-interactive (no navigation, no delete) until the
   * backend refetch replaces them with confirmed data.
   */
  isOptimistic?: boolean;
  /** Title not yet server-confirmed — sidebar shows a pending spinner. */
  titlePending?: boolean;
}
